import React, { useMemo, useState } from 'react';
import { Link2, Search, Unlink } from 'lucide-react';
import {
  Button,
  Card,
  EmptyState,
  ErrorState,
  Modal,
  Notice,
  StagePill,
  Tag,
  formatDate,
  isTerminalStage,
  useDesk,
  useDeskData,
  type ReferralView,
} from '@valutten/partner-desk-ui';
import { PARTNER_DEAL_CANDIDATE_GRANT_TTL_MS } from '@valutten/shared-schemas/dist/partnerNetwork';
import { Skeleton } from '../../../components/common/SkeletonLoader';
import { fetchDealCandidates } from '../../../hooks/usePartnerDesk';
import { deskErrorMessage, formatSettlementMonth, type TenantDealCandidate } from '../../../utils/partnerReferrals';

/**
 * Loan links (tenant desk only, capability crm.link; §16 item 2): connect a
 * referral of this company's own program to its loan in the company's CRM.
 *
 *   1. "Find deals" asks tenantGetPartnerReferralDealCandidates for ONE
 *      referral. The server matches on that referral's own client name (there
 *      is no search box), keeps to the caller's broker scope, returns no loan
 *      value, audits the call and rate limits it.
 *   2. Picking a deal asks for confirmation, then partnerLinkCrmDeal links it.
 *      The server accepts only a deal it showed this caller in the last 30
 *      minutes; after that the deals must be found again.
 *   3. Unlink asks for confirmation, then partnerUnlinkCrmDeal.
 */

type Filter = 'unlinked' | 'linked' | 'all';

const FILTERS: { key: Filter; label: string }[] = [
  { key: 'unlinked', label: 'Not linked' },
  { key: 'linked', label: 'Linked' },
  { key: 'all', label: 'All' },
];

/** Which referrals a filter shows. Rejected referrals never need a loan link. Pure, so it is unit-tested. */
export function loanLinkRows(referrals: ReferralView[], filter: Filter): ReferralView[] {
  const live = referrals.filter((r) => r.verification !== 'rejected');
  if (filter === 'linked') return live.filter((r) => !!r.crmLink);
  if (filter === 'unlinked') return live.filter((r) => !r.crmLink && !isTerminalStage(r.stage));
  return live;
}

interface Candidates {
  referralId: string;
  deals: TenantDealCandidate[];
  /** When the server's grant for these deals runs out (ms since epoch). */
  expiresAtMs: number;
}

type Confirm =
  | { kind: 'link'; referral: ReferralView; deal: TenantDealCandidate }
  | { kind: 'unlink'; referral: ReferralView };

export const LoanLinksPage: React.FC<{ partnerId: string; now?: () => number }> = ({ partnerId, now = Date.now }) => {
  const { api, PageHeader, PageBody } = useDesk();
  const { referrals } = useDeskData();
  const [filter, setFilter] = useState<Filter>('unlinked');
  const [candidates, setCandidates] = useState<Candidates | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<Confirm | null>(null);

  const rows = useMemo(() => loanLinkRows(referrals.data?.referrals ?? [], filter), [referrals.data, filter]);
  const crm = api.crm;

  const find = async (r: ReferralView) => {
    setBusy(`find:${r.id}`);
    setError(null);
    setNotice(null);
    try {
      const res = await fetchDealCandidates(partnerId, r.id);
      setCandidates({
        referralId: r.id,
        deals: res.candidates,
        expiresAtMs: res.grantExpiresAtMs ?? now() + PARTNER_DEAL_CANDIDATE_GRANT_TTL_MS,
      });
    } catch (err) {
      setCandidates(null);
      setError(deskErrorMessage(err, 'The CRM could not be checked. Please try again.'));
    } finally {
      setBusy(null);
    }
  };

  const confirmAction = async () => {
    if (!confirm || !crm) return;
    const c = confirm;
    setBusy('confirm');
    setError(null);
    try {
      if (c.kind === 'link') {
        await crm.linkDeal({ referralId: c.referral.id, dealId: c.deal.dealId });
        setNotice(`${c.referral.clientName} is linked to deal ${c.deal.dealId}.`);
      } else {
        await crm.unlinkDeal({ referralId: c.referral.id });
        setNotice(`The CRM link for ${c.referral.clientName} is removed.`);
      }
      setCandidates(null);
      setConfirm(null);
      await referrals.reload();
    } catch (err) {
      setConfirm(null);
      setError(deskErrorMessage(err, c.kind === 'link' ? 'The deal was not linked.' : 'The link was not removed.'));
    } finally {
      setBusy(null);
    }
  };

  if (!crm) return <EmptyState title="Loan links are not available here" />;

  const header = (
    <PageHeader
      title="Loan links"
      sub="Connect each referral to its loan in your CRM so it follows the loan's stage. VALUTTEN offers only deals under the referral's own client name, within your scope."
    />
  );

  if (referrals.loading && !referrals.data) {
    return (
      <PageBody>
        {header}
        <div className="space-y-3" aria-busy="true">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </div>
      </PageBody>
    );
  }
  if (referrals.error && !referrals.data) {
    return (
      <PageBody>
        {header}
        <ErrorState message={referrals.error} onRetry={() => void referrals.reload()} />
      </PageBody>
    );
  }

  const expired = candidates ? now() >= candidates.expiresAtMs : false;

  return (
    <PageBody>
      {header}
      <div className="mb-4 flex flex-wrap gap-2" role="group" aria-label="Show">
        {FILTERS.map((f) => (
          <Button key={f.key} size="sm" variant={filter === f.key ? 'primary' : 'secondary'} aria-pressed={filter === f.key} onClick={() => setFilter(f.key)}>
            {f.label}
          </Button>
        ))}
      </div>
      {notice && (
        <div className="mb-3">
          <Notice tone="success">{notice}</Notice>
        </div>
      )}
      {error && (
        <div className="mb-3">
          <Notice tone="error">{error}</Notice>
        </div>
      )}
      <Card flush>
        {rows.length === 0 ? (
          <EmptyState
            title={filter === 'linked' ? 'No referral is linked yet' : 'Nothing to link'}
            body={filter === 'unlinked' ? 'Every open referral is linked to its loan.' : undefined}
          />
        ) : (
          <ul className="divide-y divide-on-surface/10">
            {rows.map((r) => {
              const open = candidates?.referralId === r.id;
              return (
                <li key={r.id} className="px-4 py-3 sm:px-5">
                  <div className="flex flex-wrap items-center gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="truncate font-semibold text-on-surface">{r.clientName}</div>
                      <div className="text-xs text-on-surface-variant">Referred {formatDate(r.createdAt)}</div>
                    </div>
                    <StagePill stage={r.stage} />
                    {r.crmLink ? (
                      <>
                        <Tag tone="blue">
                          {r.crmLink.provider} deal {r.crmLink.dealId}
                        </Tag>
                        <Button size="sm" variant="ghost" onClick={() => setConfirm({ kind: 'unlink', referral: r })} disabled={!!busy}>
                          <Unlink className="h-4 w-4" aria-hidden="true" /> Unlink
                        </Button>
                      </>
                    ) : (
                      <Button size="sm" onClick={() => void find(r)} loading={busy === `find:${r.id}`} disabled={!!busy && busy !== `find:${r.id}`}>
                        <Search className="h-4 w-4" aria-hidden="true" /> Find deals
                      </Button>
                    )}
                  </div>
                  {open && candidates && !r.crmLink && (
                    <div className="mt-3 rounded-card-xs ring-1 ring-on-surface/10">
                      {expired ? (
                        <div className="flex flex-wrap items-center gap-2 px-3 py-2 text-sm text-on-surface-variant">
                          These deals were found more than 30 minutes ago. Find them again to link one.
                        </div>
                      ) : candidates.deals.length === 0 ? (
                        <div className="px-3 py-2 text-sm text-on-surface-variant">No deals under this client&rsquo;s name in your scope.</div>
                      ) : (
                        <ul className="divide-y divide-on-surface/10" aria-label={`Deals for ${r.clientName}`}>
                          {candidates.deals.map((d) => (
                            <li key={d.dealId} className="flex flex-wrap items-center gap-2 px-3 py-2 text-sm">
                              <div className="min-w-0 flex-1">
                                <div className="truncate font-semibold text-on-surface">{d.borrowerName ?? `Deal ${d.dealId}`}</div>
                                <div className="truncate text-xs text-on-surface-variant">
                                  {[d.provider, `deal ${d.dealId}`, d.status, d.adviserName, formatSettlementMonth(d.settlementMonth) ? `settled ${formatSettlementMonth(d.settlementMonth)}` : null]
                                    .filter(Boolean)
                                    .join(' · ')}
                                </div>
                              </div>
                              <Button size="sm" variant="primary" onClick={() => setConfirm({ kind: 'link', referral: r, deal: d })} disabled={!!busy}>
                                <Link2 className="h-4 w-4" aria-hidden="true" /> Link
                              </Button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </Card>

      <Modal
        open={!!confirm}
        title={confirm?.kind === 'unlink' ? 'Remove the CRM link?' : 'Link this deal?'}
        onClose={() => (busy === 'confirm' ? undefined : setConfirm(null))}
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirm(null)} disabled={busy === 'confirm'}>
              Cancel
            </Button>
            <Button variant={confirm?.kind === 'unlink' ? 'danger' : 'primary'} onClick={() => void confirmAction()} loading={busy === 'confirm'}>
              {confirm?.kind === 'unlink' ? 'Confirm unlink' : 'Confirm link'}
            </Button>
          </>
        }
      >
        {confirm?.kind === 'link' && (
          <p className="text-sm text-on-surface">
            Link {confirm.referral.clientName}&rsquo;s referral to {confirm.deal.borrowerName ?? 'the deal'} ({confirm.deal.provider} deal{' '}
            {confirm.deal.dealId})? From then on the referral follows that deal&rsquo;s CRM stage.
          </p>
        )}
        {confirm?.kind === 'unlink' && (
          <p className="text-sm text-on-surface">
            Remove the link between {confirm.referral.clientName}&rsquo;s referral and {confirm.referral.crmLink?.provider} deal{' '}
            {confirm.referral.crmLink?.dealId}? The referral stops following the CRM until it is linked again.
          </p>
        )}
      </Modal>
    </PageBody>
  );
};

export default LoanLinksPage;
